import React, { useState } from 'react';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';

function ChangePasswordCard({ api }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg('');
    if (form.new_password.length < 8) { setMsg('New password must be at least 8 characters.'); return; }
    if (form.new_password !== form.confirm_password) { setMsg('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await api.put('/auth/staff/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      setMsg('Password updated.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err.message || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <span className="card-title">Change Password</span>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit} style={{ maxWidth: 400 }}>
          {msg && <div className={`alert alert-${msg === 'Password updated.' ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>{msg}</div>}
          <div className="form-group">
            <label>Current Password</label>
            <input type="password" className="form-control" value={form.current_password} autoComplete="current-password"
              onChange={e => setForm(p => ({ ...p, current_password: e.target.value }))} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>New Password</label>
              <input type="password" className="form-control" value={form.new_password} autoComplete="new-password"
                placeholder="Min 8 characters"
                onChange={e => setForm(p => ({ ...p, new_password: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" className="form-control" value={form.confirm_password} autoComplete="new-password"
                onChange={e => setForm(p => ({ ...p, confirm_password: e.target.value }))} required />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <span className="spinner" /> : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AccountPage() {
  const api = useApi();
  const { user, updateUser } = useAuth();

  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  async function handleSaveProfile(e) {
    e.preventDefault();
    setProfileMsg('');
    setProfileSaving(true);
    try {
      const updated = await api.put('/auth/staff/profile', {
        name: profileForm.name,
        email: profileForm.email,
      });
      updateUser({ name: updated.name, email: updated.email });
      setProfileMsg('Profile updated.');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      setProfileMsg(err.message || 'Failed to update profile.');
    } finally {
      setProfileSaving(false);
    }
  }

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Account</h1>
          <p className="page-subtitle">Update your name, email, and password</p>
        </div>
      </div>

      {/* Profile */}
      <div className="card mb-4">
        <div className="card-header">
          <span className="card-title">Profile</span>
        </div>
        <div className="card-body">
          <form onSubmit={handleSaveProfile} style={{ maxWidth: 400 }}>
            {profileMsg && (
              <div className={`alert alert-${profileMsg === 'Profile updated.' ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>
                {profileMsg}
              </div>
            )}
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                className="form-control"
                value={profileForm.name}
                onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                value={profileForm.email}
                onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                autoComplete="email"
              />
              <div className="form-hint">Used to log in to this app</div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={profileSaving}>
              {profileSaving ? <span className="spinner" /> : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>

      {/* Password */}
      <ChangePasswordCard api={api} />
    </div>
  );
}
